// Style Essence Data - Based on Kitchener/McJimsey 7 Essence System
// High-quality, comprehensive data for SEO-optimized pages

export interface StyleEssence {
  id: string;
  name: string;
  yinYang: "yang" | "yin" | "balanced";
  yinYangLevel: string; // e.g., "Pure Yang", "Soft Yin", "Balanced"
  tagline: string;
  description: string;
  extendedDescription: string;
  facialFeatures: string[];
  bodyCharacteristics: string[];
  energyWords: string[];
  styleKeywords: string[];
  colorPalette: { name: string; hex: string; description: string }[];
  keyPieces: string[];
  fabricsAndTextures: string[];
  patternsAndPrints: string[];
  accessories: string[];
  avoidStyles: string[];
  celebrities: { name: string; description: string }[];
  outfitIdeas: { name: string; occasion: string; description: string; pieces: string[] }[];
  makeupStyle: string;
  hairstyles: string[];
  commonBlends: { essence: string; description: string }[];
  vsOtherEssences: { essence: string; difference: string }[];
  faqs: { question: string; answer: string }[];
  seasonConnection: string; // How it relates to color seasons
  kibbeConnection: string; // How it relates to Kibbe types
}

export const styleEssences: Record<string, StyleEssence> = {
  dramatic: {
    id: "dramatic",
    name: "Dramatic",
    yinYang: "yang",
    yinYangLevel: "Pure Yang",
    tagline: "Bold, powerful, and unapologetically striking.",
    description:
      "The Dramatic essence is the most yang of all essences, characterized by sharp angles, bold presence, and commanding energy. People with Dramatic essence have striking features that demand attention—high cheekbones, angular jawlines, and intense eyes. They look best in structured, architectural clothing that matches their powerful presence.",
    extendedDescription: `The Dramatic essence embodies power, intensity, and boldness. If you have this essence, you likely have sharp, angular features and a presence that naturally commands attention when you enter a room. Your energy is confident, authoritative, and sometimes intimidating in the best way.

Dramatic essence is about making a statement. Minimalism works beautifully for you, but it needs to be bold minimalism—strong silhouettes, sharp lines, and striking contrasts. You're not meant for fussy details or soft, romantic frills. Your style is sleek, sophisticated, and powerful.

Think of the red carpet looks that stop everyone in their tracks—architectural gowns, sharp-shouldered blazers, dramatic floor-length silhouettes. That's Dramatic essence at its finest. You carry bold choices with ease because your features and presence can support them.`,
    facialFeatures: [
      "Sharp, angular bone structure",
      "Prominent cheekbones",
      "Strong, defined jawline",
      "Long, straight nose",
      "Intense, deep-set eyes",
      "Thin, straight lips",
      "High forehead",
      "Elongated face shape",
    ],
    bodyCharacteristics: [
      "Long vertical line",
      "Narrow frame",
      "Sharp shoulders",
      "Long limbs",
      "Angular silhouette",
      "Minimal curves",
      "Tall or gives impression of height",
    ],
    energyWords: [
      "Powerful",
      "Intense",
      "Commanding",
      "Sophisticated",
      "Bold",
      "Striking",
      "Authoritative",
      "Confident",
      "Elegant",
      "Mysterious",
    ],
    styleKeywords: [
      "Architectural",
      "Sleek",
      "Minimalist",
      "Sharp",
      "Structural",
      "Bold",
      "Geometric",
      "Monochromatic",
      "Statement",
      "Powerful",
    ],
    colorPalette: [
      { name: "Black", hex: "#000000", description: "Your power color—sharp, bold, and endlessly sophisticated" },
      { name: "Pure White", hex: "#FFFFFF", description: "Crisp contrast that emphasizes your angular features" },
      { name: "Deep Red", hex: "#8B0000", description: "Intense and commanding for maximum impact" },
      { name: "Royal Purple", hex: "#4B0082", description: "Regal and mysterious, perfect for evening" },
      { name: "Emerald", hex: "#046307", description: "Rich and striking without being harsh" },
      { name: "Silver", hex: "#C0C0C0", description: "Metallic edge that amplifies your modern energy" },
    ],
    keyPieces: [
      "Structured blazer with sharp shoulders",
      "Floor-length column gown",
      "Tailored wide-leg trousers",
      "Sleek leather jacket",
      "Architectural midi dress",
      "High-waisted pencil skirt",
      "Long, dramatic coat",
      "Fitted turtleneck",
      "Sharp-pointed pumps",
      "Minimalist sheath dress",
    ],
    fabricsAndTextures: [
      "Crisp cotton",
      "Structured wool",
      "Stiff silk",
      "Leather",
      "Sharp gabardine",
      "High-shine satin",
      "Sleek jersey",
      "Patent leather",
      "Architectural knits",
    ],
    patternsAndPrints: [
      "Bold geometric prints",
      "Sharp stripes",
      "Abstract art prints",
      "Color blocking",
      "Large-scale patterns",
      "Graphic black and white",
      "Angular animal prints",
    ],
    accessories: [
      "Architectural statement jewelry",
      "Geometric earrings",
      "Cuff bracelets",
      "Structured handbags",
      "Pointed-toe shoes",
      "Minimalist watches",
      "Bold sunglasses",
      "Angular belts",
    ],
    avoidStyles: [
      "Soft, flowing fabrics",
      "Rounded shapes and silhouettes",
      "Fussy, ornate details",
      "Ruffles and frills",
      "Small, delicate patterns",
      "Cute or sweet styling",
      "Casual, relaxed fits",
      "Overly romantic elements",
    ],
    celebrities: [
      { name: "Tilda Swinton", description: "The ultimate Dramatic—angular, otherworldly, and impossibly chic in architectural fashion." },
      { name: "Cate Blanchett", description: "Sharp features and commanding presence; masters structured, elegant pieces." },
      { name: "Angelina Jolie", description: "Bold bone structure and intense eyes; embodies powerful, striking glamour." },
      { name: "Cher", description: "Legendary Dramatic essence; has worn the most iconic architectural fashion moments." },
      { name: "Grace Jones", description: "Pure Dramatic energy—sharp, bold, and utterly commanding." },
    ],
    outfitIdeas: [
      {
        name: "Power Executive",
        occasion: "Work/Business",
        description: "Command the boardroom with sharp, structured pieces",
        pieces: ["Black sharp-shouldered blazer", "White silk blouse", "High-waisted tailored trousers", "Pointed-toe pumps", "Geometric gold earrings"],
      },
      {
        name: "Evening Elegance",
        occasion: "Formal Events",
        description: "Red carpet-worthy drama",
        pieces: ["Floor-length column gown in deep red", "Statement cuff bracelet", "Strappy stiletto heels", "Sleek updo", "Bold lip"],
      },
      {
        name: "Urban Sophisticate",
        occasion: "Everyday/Casual",
        description: "Elevated casual with Dramatic edge",
        pieces: ["Long leather trench coat", "Black turtleneck", "Straight-leg dark jeans", "Ankle boots", "Minimalist sunglasses"],
      },
    ],
    makeupStyle:
      "Bold, defined, and striking. Strong brows, sharp contour, dramatic eye looks (smoky eyes, graphic liner), bold lip colors. Avoid soft, blended, or 'no-makeup makeup' looks—your features demand definition.",
    hairstyles: [
      "Sleek, straight styles",
      "Sharp bobs",
      "Slicked-back looks",
      "Dramatic updos",
      "Long, one-length cuts",
      "Asymmetrical cuts",
      "Bold color (jet black, platinum)",
    ],
    commonBlends: [
      { essence: "Classic", description: "Dramatic Classic—sophisticated with sharp edges. Think Jackie Kennedy's most architectural moments." },
      { essence: "Natural", description: "Dramatic Natural—powerful but with an effortless, unconstructed edge." },
      { essence: "Ethereal", description: "Dramatic Ethereal—otherworldly and striking, like a high fashion editorial." },
    ],
    vsOtherEssences: [
      { essence: "Natural", difference: "Dramatic is sharp and structured; Natural is relaxed and unconstructed. Dramatic needs polish; Natural needs ease." },
      { essence: "Classic", difference: "Dramatic is bold and extreme; Classic is balanced and understated. Dramatic makes statements; Classic maintains harmony." },
      { essence: "Romantic", difference: "Dramatic is angular and powerful; Romantic is curved and soft. Complete opposites on the yin-yang spectrum." },
    ],
    faqs: [
      {
        question: "What defines the Dramatic style essence?",
        answer:
          "The Dramatic style essence is the most yang (masculine energy) of the seven style essences. People with Dramatic essence have sharp, angular features, commanding presence, and look best in bold, architectural clothing with clean lines and striking silhouettes. Think structured blazers, column gowns, and minimalist-but-powerful pieces.",
      },
      {
        question: "What facial features indicate Dramatic essence?",
        answer:
          "Dramatic essence shows through sharp, angular bone structure (prominent cheekbones, defined jawline), intense or deep-set eyes, a long vertical line in your body, and a naturally commanding presence. People with this essence often look overwhelmed by soft, romantic clothing and shine in sharp, structural pieces.",
      },
      {
        question: "Which styles should Dramatics avoid?",
        answer:
          "Dramatics should avoid soft, flowing fabrics, rounded silhouettes, fussy or ornate details, ruffles and frills, small delicate patterns, and overly romantic or cute styling. These elements fight against your natural sharpness and can make you look like you're wearing a costume rather than owning your look.",
      },
      {
        question: "Can shorter people have Dramatic essence?",
        answer:
          "Yes! While many Dramatics are tall, essence is primarily about facial features and energy, not height. A petite person with sharp, angular features and intense presence can absolutely have Dramatic essence. You might be Dramatic with Gamine influence, or have Dramatic as a secondary essence.",
      },
      {
        question: "How does Dramatic essence differ from Kibbe Dramatic body type?",
        answer:
          "Kibbe Dramatic focuses on body line and bone structure for clothing recommendations. Style essence Dramatic is about your overall visual impression—facial features, energy, and the 'vibe' you give off. You could be a Kibbe Dramatic with Dramatic essence (common), or have mismatched body type and essence (also possible).",
      },
    ],
    seasonConnection:
      "Dramatic essence works beautifully with bold, high-contrast color seasons like Dark Winter, Bright Winter, and Bright Spring. The intensity of these palettes matches Dramatic's powerful presence.",
    kibbeConnection:
      "Often found in Kibbe Dramatic, Soft Dramatic, and Dramatic Classic body types. However, essence is separate from body type—a Soft Natural could have Dramatic essence if their facial features are sharp and commanding.",
  },

  natural: {
    id: "natural",
    name: "Natural",
    yinYang: "yang",
    yinYangLevel: "Relaxed Yang",
    tagline: "Effortlessly cool, laid-back, and authentically approachable.",
    description:
      "The Natural essence embodies casual yang energy—relaxed, approachable, and effortlessly stylish. People with Natural essence have broad, blunt features and an easygoing presence that looks best in unconstructed, comfortable clothing that moves with them rather than constraining them.",
    extendedDescription: `Natural essence is the laid-back cousin of Dramatic. While both are yang, Natural has a softer, more approachable energy. If you have this essence, you probably look best with your hair slightly tousled, your outfit slightly relaxed, and your overall vibe saying 'I threw this together but it works.'

The key to Natural essence is ease. Stiff, overly structured clothing fights against your energy. You need room to move, breathe, and be yourself. Think soft blazers, flowing layers, unconstructed coats, and textures that feel as good as they look.

Natural essence people often look like they could be headed to a beach, a hike, or a casual brunch—even when they're dressed up. Your beauty is in your approachability and authenticity. You're the friend everyone trusts because you seem so genuine.`,
    facialFeatures: [
      "Broad, open bone structure",
      "Wide-set eyes",
      "Blunt or softened features",
      "Strong but relaxed jawline",
      "Broad nose",
      "Full or relaxed lips",
      "Open, friendly expression",
      "Minimal sharpness",
    ],
    bodyCharacteristics: [
      "Broad shoulders",
      "Athletic or sturdy frame",
      "Moderate to long vertical line",
      "Blunt bone edges",
      "Width in frame",
      "Relaxed posture",
      "Strong but unconstructed appearance",
    ],
    energyWords: [
      "Approachable",
      "Relaxed",
      "Authentic",
      "Casual",
      "Friendly",
      "Effortless",
      "Down-to-earth",
      "Cool",
      "Easy-going",
      "Unpretentious",
    ],
    styleKeywords: [
      "Relaxed",
      "Unconstructed",
      "Layered",
      "Textured",
      "Casual",
      "Organic",
      "Effortless",
      "Natural fibers",
      "Comfortable",
      "Easy",
    ],
    colorPalette: [
      { name: "Olive", hex: "#808000", description: "Earthy and natural, perfect for your grounded energy" },
      { name: "Camel", hex: "#C19A6B", description: "Warm neutral that feels effortless and approachable" },
      { name: "Denim Blue", hex: "#1560BD", description: "Classic and casual, a Natural staple" },
      { name: "Forest Green", hex: "#228B22", description: "Nature-inspired depth that suits your essence" },
      { name: "Cream", hex: "#FFFDD0", description: "Soft white that's relaxed rather than crisp" },
      { name: "Terracotta", hex: "#E2725B", description: "Warm earth tone with casual sophistication" },
    ],
    keyPieces: [
      "Oversized linen blazer",
      "Relaxed straight-leg jeans",
      "Chunky knit sweater",
      "Leather or suede jacket",
      "Wide-leg trousers",
      "Flowing maxi dress",
      "Unconstructed coat",
      "Cotton button-down shirt",
      "Comfortable loafers",
      "Wrap dresses and tops",
    ],
    fabricsAndTextures: [
      "Linen",
      "Cotton",
      "Suede",
      "Soft leather",
      "Chunky knits",
      "Denim",
      "Chambray",
      "Cashmere",
      "Wool blends",
      "Raw silk",
    ],
    patternsAndPrints: [
      "Nature-inspired prints",
      "Subtle stripes",
      "Organic patterns",
      "Muted florals",
      "Abstract natural prints",
      "Tie-dye",
      "Batik",
      "Earthy geometrics",
    ],
    accessories: [
      "Leather tote bags",
      "Woven accessories",
      "Natural stone jewelry",
      "Simple gold or silver pieces",
      "Comfortable sandals",
      "Canvas sneakers",
      "Straw hats",
      "Leather belts",
    ],
    avoidStyles: [
      "Stiff, overly structured pieces",
      "Fussy, ornate details",
      "Tight, restrictive clothing",
      "Overly polished looks",
      "Delicate, fragile fabrics",
      "Sharp, angular shapes",
      "Formal, stiff styling",
      "Excessive accessories",
    ],
    celebrities: [
      { name: "Jennifer Aniston", description: "The quintessential Natural—approachable, effortless, and timelessly casual-chic." },
      { name: "Julia Roberts", description: "Broad features, warm smile, and that signature relaxed glamour." },
      { name: "Cameron Diaz", description: "Athletic, easy-going, and naturally beautiful without trying." },
      { name: "Cindy Crawford", description: "Supermodel with Natural essence—athletic, approachable, and effortlessly stunning." },
      { name: "Jessica Alba", description: "Relaxed beauty with friendly energy; looks best in easy, comfortable pieces." },
    ],
    outfitIdeas: [
      {
        name: "Weekend Ready",
        occasion: "Casual/Errands",
        description: "Effortlessly put-together for everyday life",
        pieces: ["Oversized linen shirt", "Relaxed straight-leg jeans", "Leather sandals", "Woven tote bag", "Simple gold hoops"],
      },
      {
        name: "Relaxed Professional",
        occasion: "Smart Casual Work",
        description: "Professional but never stiff",
        pieces: ["Soft unstructured blazer", "Silk button-down", "Wide-leg trousers", "Leather loafers", "Minimal jewelry"],
      },
      {
        name: "Elevated Evening",
        occasion: "Dinner/Events",
        description: "Dressed up the Natural way",
        pieces: ["Flowing maxi dress", "Leather belt", "Strappy flat sandals", "Natural stone earrings", "Loose waves hairstyle"],
      },
    ],
    makeupStyle:
      "Fresh, minimal, and natural-looking. Glowing skin, soft brows, neutral tones on eyes and lips. The goal is to look like you're not wearing much (even if you are). Avoid heavy, dramatic makeup—it fights your essence.",
    hairstyles: [
      "Loose, natural waves",
      "Effortless beach waves",
      "Relaxed updos",
      "Air-dried texture",
      "Long, flowing layers",
      "Low ponytails",
      "Natural hair color",
    ],
    commonBlends: [
      { essence: "Classic", description: "Natural Classic—relaxed elegance with timeless ease. Think Ralph Lauren aesthetic." },
      { essence: "Romantic", description: "Natural Romantic (Soft Natural)—approachable softness with a sensual edge." },
      { essence: "Dramatic", description: "Natural Dramatic—powerful but unconstructed, like an off-duty supermodel." },
    ],
    vsOtherEssences: [
      { essence: "Dramatic", difference: "Natural is relaxed and approachable; Dramatic is sharp and commanding. Natural flows; Dramatic structures." },
      { essence: "Classic", difference: "Natural is casual and unconstructed; Classic is polished and balanced. Natural wrinkles welcome; Classic stays pressed." },
      { essence: "Gamine", difference: "Natural is relaxed and flowing; Gamine is sharp and playful. Natural is effortless; Gamine is spirited." },
    ],
    faqs: [
      {
        question: "What makes someone a Natural essence?",
        answer:
          "The Natural style essence represents relaxed yang energy—approachable, casual, and effortlessly stylish. People with Natural essence have broad, open features and look best in unconstructed, comfortable clothing made from natural fabrics. Think linen blazers, relaxed jeans, and easy layers.",
      },
      {
        question: "What are the signs of Natural essence?",
        answer:
          "Signs include broad or open bone structure, wide-set eyes, an approachable/friendly vibe, and feeling most yourself in comfortable, unconstructed clothing. People often describe you as down-to-earth or easy-going. Stiff, formal clothing makes you feel like you're in costume.",
      },
      {
        question: "Natural essence vs Kibbe Natural: what's the difference?",
        answer:
          "Kibbe Natural types focus on body structure—width, blunt bones, and how clothes should accommodate that. Natural essence is about your overall energy and facial features—the approachable, relaxed, authentic vibe you project. You can have Natural essence with a different Kibbe type, or be Kibbe Natural with a different essence.",
      },
      {
        question: "How do Naturals dress up for formal events?",
        answer:
          "The key is choosing dressy pieces with a relaxed quality—flowing maxi dresses, unconstructed evening separates, soft suits. Avoid stiff formal wear that fights your natural ease. Think elegant but approachable, not rigid or overly polished.",
      },
    ],
    seasonConnection:
      "Natural essence harmonizes with earthy, muted color seasons like Soft Autumn, True Autumn, and Soft Summer. These palettes have the organic, unpretentious quality that matches Natural's energy.",
    kibbeConnection:
      "Commonly found in Kibbe Natural, Soft Natural, and Flamboyant Natural body types. The relaxed yang of Natural essence often aligns with Kibbe's Natural family.",
  },

  gamine: {
    id: "gamine",
    name: "Gamine",
    yinYang: "yang",
    yinYangLevel: "Playful Yang",
    tagline: "Youthful, spirited, and charmingly unconventional.",
    description:
      "The Gamine essence is yang with a twist—sharp and angular but playful, youthful, and full of spirit. People with Gamine essence have a mix of contrasts in their features, creating an interesting, eye-catching appearance. They look best in bold contrasts, cropped lengths, and unexpected combinations.",
    extendedDescription: `Gamine essence is the rebel of the style world. If you have this essence, you have that special something—a spark, a mischief, a youthful energy that refuses to be boring. Your features likely have interesting contrasts: maybe large eyes with a sharp jaw, or delicate features with angular bones.

The key to Gamine style is contrast and surprise. You look amazing in bold color combinations, unexpected mixes (masculine meets feminine, casual meets dressy), and pieces that have a youthful, spirited quality. Cropped lengths flatter you—cropped jackets, ankle-length pants, above-knee skirts.

Gamine essence people never look quite right in flowing, monotonous outfits. You need visual interest, playfulness, and that unexpected element. Think of your style as the fashion equivalent of a wink—classic with a twist, polished with personality.`,
    facialFeatures: [
      "Mixture of yin and yang features",
      "Large, expressive eyes",
      "Angular or sharp bone structure",
      "Small or compact facial features",
      "Interesting contrasts",
      "Youthful appearance",
      "Sharp or upturned nose",
      "Defined but petite features",
    ],
    bodyCharacteristics: [
      "Petite or compact frame",
      "Short to moderate vertical line",
      "Angular bones with softness",
      "Mix of sharp and rounded",
      "Small hands and feet",
      "Youthful proportions",
      "Lean or athletic build",
    ],
    energyWords: [
      "Youthful",
      "Spirited",
      "Playful",
      "Mischievous",
      "Energetic",
      "Fresh",
      "Unconventional",
      "Rebellious",
      "Charming",
      "Quirky",
    ],
    styleKeywords: [
      "Cropped",
      "Contrasting",
      "Playful",
      "Bold",
      "Mixed",
      "Youthful",
      "Graphic",
      "Unexpected",
      "Compact",
      "Spirited",
    ],
    colorPalette: [
      { name: "True Red", hex: "#FF0000", description: "Bold and playful, perfect for your spirited energy" },
      { name: "Navy", hex: "#000080", description: "Classic contrast base that's never boring" },
      { name: "Bright White", hex: "#FFFFFF", description: "Crisp and fresh for high contrast" },
      { name: "Cobalt Blue", hex: "#0047AB", description: "Vivid and youthful, makes a statement" },
      { name: "Kelly Green", hex: "#4CBB17", description: "Fresh and spirited, perfect for your energy" },
      { name: "Sunshine Yellow", hex: "#FFDA03", description: "Playful and attention-grabbing" },
    ],
    keyPieces: [
      "Cropped jacket",
      "High-waisted pants",
      "Peter Pan collar blouse",
      "Breton striped top",
      "Shift dress",
      "Tailored shorts",
      "Fitted blazer",
      "Ankle boots",
      "Structured mini bag",
      "Bold graphic tees",
    ],
    fabricsAndTextures: [
      "Crisp cotton",
      "Denim",
      "Leather accents",
      "Sharp tweeds",
      "Ponte",
      "Structured knits",
      "Cotton poplin",
      "Mix of textures",
    ],
    patternsAndPrints: [
      "Bold stripes",
      "Color blocking",
      "Graphic prints",
      "Polka dots",
      "Geometric patterns",
      "Mixed prints",
      "Checks and plaids",
      "Sharp contrast patterns",
    ],
    accessories: [
      "Structured small bags",
      "Bold earrings",
      "Hair accessories (clips, headbands)",
      "Interesting belts",
      "Ankle boots",
      "Oxford shoes",
      "Statement glasses",
      "Contrasting scarves",
    ],
    avoidStyles: [
      "Long, flowing pieces",
      "Monotonous color schemes",
      "Overly romantic or soft styling",
      "Maxi lengths",
      "Boring, safe choices",
      "Overly mature looks",
      "Baggy, shapeless clothes",
      "Minimalist-only outfits",
    ],
    celebrities: [
      { name: "Audrey Hepburn", description: "The original Gamine icon—elegant, playful, and eternally youthful." },
      { name: "Emma Watson", description: "Sharp features with youthful charm; masters the cropped, contrasting Gamine aesthetic." },
      { name: "Lily Collins", description: "Large eyes, defined features, and that playful-yet-polished Gamine energy." },
      { name: "Winona Ryder", description: "Pixie-ish charm with edgy undertones—classic Gamine mix." },
      { name: "Zooey Deschanel", description: "Large eyes, youthful features, and quirky style that embodies Gamine spirit." },
    ],
    outfitIdeas: [
      {
        name: "Classic with a Twist",
        occasion: "Work/Smart Casual",
        description: "Professional but never boring",
        pieces: ["Cropped blazer in bold color", "High-waisted tailored trousers", "Breton striped top", "Oxford shoes", "Structured mini bag"],
      },
      {
        name: "Weekend Spirited",
        occasion: "Casual/Fun",
        description: "Playful casual that shows your personality",
        pieces: ["Graphic tee", "High-waisted mom jeans", "Cropped leather jacket", "Ankle boots", "Bold earrings"],
      },
      {
        name: "Evening Edge",
        occasion: "Date Night/Events",
        description: "Dressed up the Gamine way",
        pieces: ["Tailored jumpsuit with contrast details", "Statement belt", "Pointed-toe flats", "Geometric jewelry", "Bold lip color"],
      },
    ],
    makeupStyle:
      "Fresh, defined, and playful. Bold brows, defined eyes (graphic liner works great), and lips can be bold OR natural—contrast is key. Think youthful and polished rather than heavy or overly glamorous.",
    hairstyles: [
      "Pixie cuts",
      "Sharp bobs",
      "Cropped styles",
      "Bangs (especially blunt)",
      "Sleek short styles",
      "Boyish cuts with feminine touches",
      "Bold hair accessories",
    ],
    commonBlends: [
      { essence: "Classic", description: "Gamine Classic—polished with a playful edge. Think Audrey Hepburn sophistication." },
      { essence: "Romantic", description: "Soft Gamine—playful contrast with soft, feminine touches." },
      { essence: "Dramatic", description: "Flamboyant Gamine—bold, edgy, and high-impact contrast." },
    ],
    vsOtherEssences: [
      { essence: "Ingenue", difference: "Gamine is playful and sharp; Ingenue is sweet and delicate. Gamine has edge; Ingenue has innocence." },
      { essence: "Natural", difference: "Gamine is structured and compact; Natural is relaxed and flowing. Gamine creates contrast; Natural creates ease." },
      { essence: "Classic", difference: "Gamine breaks rules with contrast; Classic follows rules with balance. Gamine surprises; Classic reassures." },
    ],
    faqs: [
      {
        question: "Who has Gamine style essence?",
        answer:
          "Gamine is a yang essence with playful, youthful energy. People with Gamine essence have contrasting features (mix of sharp and soft), a spirited presence, and look best in bold contrasts, cropped lengths, and unexpected style combinations. Think Audrey Hepburn—elegant but never boring.",
      },
      {
        question: "What are telltale Gamine features?",
        answer:
          "Look for contrasting features (like large eyes with angular bones), a youthful or spirited presence, and feeling most alive in bold contrasts and unexpected combinations. Long, flowing, monotonous outfits make Gamines feel boring and washed out.",
      },
      {
        question: "Do you have to be petite to be Gamine?",
        answer:
          "No! While many Gamines are petite, essence is primarily about facial features and energy. A tall person with contrasting features, playful energy, and that youthful spark can have Gamine essence. You might blend it with Dramatic (Flamboyant Gamine energy) for your vertical line.",
      },
      {
        question: "Gamine vs Ingenue: how are they different?",
        answer:
          "Both are youthful, but Gamine has yang sharpness and playful contrast—think mischievous. Ingenue is pure yin sweetness and delicacy—think innocent. Gamine is the pixie with attitude; Ingenue is the sweet maiden. Gamine wears cropped leather; Ingenue wears lace and ribbons.",
      },
    ],
    seasonConnection:
      "Gamine essence loves high-contrast color seasons like Bright Winter, Bright Spring, and Clear Spring. The bold, vivid colors match Gamine's spirited, high-contrast energy.",
    kibbeConnection:
      "Aligns with Kibbe Gamine, Soft Gamine, and Flamboyant Gamine body types. The contrast and compactness of Gamine essence often mirrors these body types.",
  },

  classic: {
    id: "classic",
    name: "Classic",
    yinYang: "balanced",
    yinYangLevel: "Perfectly Balanced",
    tagline: "Timeless, elegant, and effortlessly refined.",
    description:
      "The Classic essence is the only perfectly balanced essence—neither yin nor yang dominates. People with Classic essence have symmetrical, moderate features that create an impression of timeless beauty and refined elegance. They look best in balanced, harmonious outfits that avoid extremes.",
    extendedDescription: `Classic essence is the epitome of timeless elegance. If you have this essence, you probably have features that are perfectly proportioned—nothing too sharp, nothing too soft, nothing extreme. People might describe you as 'classically beautiful' or 'elegant' without being able to pinpoint exactly why.

The key to Classic style is balance and harmony. Extremes don't work for you—not too casual, not too dressy; not too boring, not too trendy; not too structured, not too flowing. You need pieces that hit the sweet spot of refined elegance.

Classic essence people look right at home in timeless pieces: the perfect blazer, the elegant sheath dress, the classic pumps. Trends come and go, but Classic essence looks best sticking to what's been proven to work. That's not boring—it's confidence in knowing what suits you.`,
    facialFeatures: [
      "Symmetrical features",
      "Moderate, balanced bone structure",
      "Neither sharp nor soft extremes",
      "Proportionate eyes, nose, mouth",
      "Even face shape (oval ideal)",
      "Regular, harmonious features",
      "Refined without being striking",
      "Ageless beauty quality",
    ],
    bodyCharacteristics: [
      "Moderate height",
      "Balanced proportions",
      "Neither angular nor curved extremes",
      "Symmetrical frame",
      "Moderate bone structure",
      "Well-proportioned limbs",
      "Neither petite nor tall extremes",
    ],
    energyWords: [
      "Elegant",
      "Refined",
      "Polished",
      "Timeless",
      "Sophisticated",
      "Graceful",
      "Composed",
      "Dignified",
      "Harmonious",
      "Understated",
    ],
    styleKeywords: [
      "Timeless",
      "Balanced",
      "Elegant",
      "Polished",
      "Refined",
      "Symmetrical",
      "Quality",
      "Classic silhouettes",
      "Understated",
      "Harmonious",
    ],
    colorPalette: [
      { name: "Navy", hex: "#000080", description: "Timeless and elegant, a Classic essential" },
      { name: "Ivory", hex: "#FFFFF0", description: "Soft white that's refined rather than stark" },
      { name: "Camel", hex: "#C19A6B", description: "Warm neutral that exudes quiet elegance" },
      { name: "Burgundy", hex: "#722F37", description: "Rich and refined, perfect for Classic sophistication" },
      { name: "Soft Grey", hex: "#A9A9A9", description: "Elegant neutral that balances any outfit" },
      { name: "Soft Rose", hex: "#E8B4B8", description: "Feminine touch that stays refined" },
    ],
    keyPieces: [
      "Tailored blazer",
      "Sheath dress",
      "Cashmere sweater",
      "Tailored trousers",
      "Pencil skirt",
      "Silk blouse",
      "Classic pumps",
      "Camel coat",
      "Pearl jewelry",
      "Structured handbag",
    ],
    fabricsAndTextures: [
      "Fine wool",
      "Silk",
      "Cashmere",
      "Quality cotton",
      "Leather",
      "Subtle textures",
      "Fine knits",
      "Smooth fabrics",
      "Quality blends",
    ],
    patternsAndPrints: [
      "Subtle stripes",
      "Small-scale patterns",
      "Houndstooth",
      "Quiet plaids",
      "Tone-on-tone patterns",
      "Minimal prints",
      "Classic checks",
      "Subtle geometrics",
    ],
    accessories: [
      "Pearl jewelry",
      "Classic watches",
      "Structured leather bags",
      "Simple gold or silver",
      "Classic pumps",
      "Silk scarves",
      "Elegant belts",
      "Timeless sunglasses",
    ],
    avoidStyles: [
      "Extreme trends",
      "Overly casual pieces",
      "Dramatic, attention-seeking styles",
      "Asymmetry and imbalance",
      "Too many patterns or textures",
      "Overly ornate details",
      "Very casual or sporty looks",
      "Anything too extreme",
    ],
    celebrities: [
      { name: "Grace Kelly", description: "The ultimate Classic icon—refined, elegant, perfectly balanced beauty." },
      { name: "Kate Middleton", description: "Modern Classic essence—polished, timeless, and always appropriate." },
      { name: "Gwyneth Paltrow", description: "Clean, refined beauty with that Classic balance and elegance." },
      { name: "Natalie Portman", description: "Symmetrical features and refined presence embody Classic essence." },
      { name: "Rosamund Pike", description: "Elegant, balanced features and sophisticated Classic style." },
    ],
    outfitIdeas: [
      {
        name: "Boardroom Elegance",
        occasion: "Work/Professional",
        description: "Commanding respect through refined polish",
        pieces: ["Tailored navy blazer", "Silk cream blouse", "Tailored trousers", "Classic pumps", "Pearl studs"],
      },
      {
        name: "Weekend Refined",
        occasion: "Casual/Errands",
        description: "Polished without being overdressed",
        pieces: ["Cashmere crewneck", "Well-fitted jeans", "Leather loafers", "Simple gold watch", "Structured tote"],
      },
      {
        name: "Evening Grace",
        occasion: "Formal/Events",
        description: "Understated glamour that never goes wrong",
        pieces: ["Elegant sheath dress", "Diamond or pearl earrings", "Classic pumps", "Clutch bag", "Subtle updo"],
      },
    ],
    makeupStyle:
      "Polished, refined, and balanced. Even, perfected skin, groomed brows, subtle definition on eyes and lips. Nothing too bold or too trendy—think timeless elegance that enhances rather than transforms.",
    hairstyles: [
      "Polished blowouts",
      "Classic bobs",
      "Elegant updos",
      "Sleek ponytails",
      "Refined waves",
      "Well-maintained cuts",
      "Classic, balanced styles",
    ],
    commonBlends: [
      { essence: "Dramatic", description: "Dramatic Classic—elegant with a powerful edge. Think Jackie Kennedy." },
      { essence: "Natural", description: "Classic Natural—refined but relaxed, approachable elegance." },
      { essence: "Romantic", description: "Soft Classic—elegant with feminine softness and grace." },
    ],
    vsOtherEssences: [
      { essence: "Dramatic", difference: "Classic is balanced and understated; Dramatic is bold and striking. Classic blends in elegantly; Dramatic stands out powerfully." },
      { essence: "Natural", difference: "Classic is polished and refined; Natural is relaxed and casual. Classic values perfection; Natural values authenticity." },
      { essence: "Gamine", difference: "Classic follows rules with balance; Gamine breaks rules with contrast. Classic is predictable elegance; Gamine is surprising charm." },
    ],
    faqs: [
      {
        question: "What does Classic essence look like?",
        answer:
          "Classic is the only perfectly balanced essence—equal yin and yang. People with Classic essence have symmetrical, moderate features and look best in timeless, balanced, harmonious clothing. Think Grace Kelly elegance: refined, polished, and never extreme.",
      },
      {
        question: "How can I tell if I'm a Classic?",
        answer:
          "Look for symmetrical, proportionate features, moderate bone structure (not sharp or soft), and a naturally refined, elegant presence. People might call you 'classically beautiful.' Trendy extremes and very casual clothes make you look off.",
      },
      {
        question: "Does Classic mean boring style?",
        answer:
          "Not at all! Classic essence is about timeless elegance, not boredom. The key is quality, perfect fit, and refined details. A Classic person in a beautifully tailored outfit is striking in their quiet confidence. The interest comes from perfection, not novelty.",
      },
      {
        question: "Should Classics follow fashion trends?",
        answer:
          "Selectively, yes. Classics can incorporate trends that align with their refined aesthetic—think a trendy color in a classic silhouette, or a subtle update to a timeless piece. Avoid extreme trends that throw off your balance.",
      },
    ],
    seasonConnection:
      "Classic essence works well with balanced, muted color seasons like Soft Summer, True Summer, and Soft Autumn. These refined palettes complement Classic's harmonious energy.",
    kibbeConnection:
      "Aligns with Kibbe Classic, Soft Classic, and Dramatic Classic body types. The balanced proportions of Classic essence often mirror these moderate body types.",
  },

  romantic: {
    id: "romantic",
    name: "Romantic",
    yinYang: "yin",
    yinYangLevel: "Sensual Yin",
    tagline: "Lush, sensual, and magnetically feminine.",
    description:
      "The Romantic essence is deep yin—soft, curved, and sensually feminine. People with Romantic essence have rounded features, full lips, large dreamy eyes, and an overall impression of lush femininity. They look best in draped, curve-honoring clothing that embraces their softness.",
    extendedDescription: `Romantic essence is the embodiment of sensual femininity. If you have this essence, you have an undeniably feminine presence—soft curves, full features, and eyes that seem to tell stories. Your beauty is lush and magnetic, drawing people in with warmth and allure.

The key to Romantic style is honoring your curves and softness. Stiff, angular clothing fights against your natural lines. You need fabrics that drape and flow, silhouettes that follow your curves, and details that are soft and feminine. Think velvet, silk, lace, and everything that feels as good as it looks.

Romantic essence people should never hide their curves or try to look more angular. Your power is in your femininity—own it. Draped necklines, fitted waists, and soft, luxurious fabrics make you look like the goddess you are.`,
    facialFeatures: [
      "Rounded bone structure",
      "Full, lush lips",
      "Large, dreamy eyes",
      "Soft, rounded cheeks",
      "Fleshy nose",
      "Rounded jawline",
      "Overall soft, curved features",
      "Sensual, inviting expression",
    ],
    bodyCharacteristics: [
      "Curved, hourglass figure",
      "Soft, fleshy appearance",
      "Rounded shoulders",
      "Full bust and/or hips",
      "Soft, rounded limbs",
      "Short to moderate height",
      "Delicate bone structure under softness",
    ],
    energyWords: [
      "Sensual",
      "Feminine",
      "Alluring",
      "Soft",
      "Romantic",
      "Dreamy",
      "Magnetic",
      "Warm",
      "Inviting",
      "Lush",
    ],
    styleKeywords: [
      "Draped",
      "Curved",
      "Soft",
      "Flowing",
      "Feminine",
      "Sensual",
      "Luxurious",
      "Fitted",
      "Ornate",
      "Tactile",
    ],
    colorPalette: [
      { name: "Deep Rose", hex: "#C21E56", description: "Romantic and feminine, your signature power color" },
      { name: "Soft Pink", hex: "#FFB6C1", description: "Sweet and feminine, perfect for Romantic softness" },
      { name: "Burgundy", hex: "#722F37", description: "Rich and sensual for evening glamour" },
      { name: "Champagne", hex: "#F7E7CE", description: "Soft, luminous, and romantically elegant" },
      { name: "Soft Lavender", hex: "#E6E6FA", description: "Dreamy and feminine, perfect for your essence" },
      { name: "Teal", hex: "#008080", description: "Rich jewel tone that flatters Romantic coloring" },
    ],
    keyPieces: [
      "Wrap dress",
      "Fitted blouse with soft details",
      "Pencil skirt",
      "Soft, draped tops",
      "Lace details",
      "Velvet pieces",
      "Curve-hugging knitwear",
      "Feminine heels",
      "Soft, structured bag",
      "Romantic jewelry",
    ],
    fabricsAndTextures: [
      "Silk",
      "Velvet",
      "Lace",
      "Cashmere",
      "Soft jersey",
      "Chiffon",
      "Satin",
      "Angora",
      "Soft suede",
      "Luxurious knits",
    ],
    patternsAndPrints: [
      "Florals (especially roses)",
      "Watercolor prints",
      "Soft, curved patterns",
      "Paisley",
      "Romantic damask",
      "Subtle animal prints",
      "Soft botanicals",
    ],
    accessories: [
      "Pearl jewelry",
      "Soft, rounded bags",
      "Feminine heels",
      "Delicate necklaces",
      "Rose gold jewelry",
      "Silk scarves",
      "Romantic earrings",
      "Soft belts",
    ],
    avoidStyles: [
      "Sharp, angular clothing",
      "Stiff, structured pieces",
      "Minimalist, sparse styling",
      "Boyish or masculine looks",
      "Harsh geometric patterns",
      "Oversized, shapeless clothes",
      "Sporty or athletic wear",
      "Anything that hides curves",
    ],
    celebrities: [
      { name: "Marilyn Monroe", description: "The ultimate Romantic icon—soft curves, dreamy eyes, magnetic femininity." },
      { name: "Scarlett Johansson", description: "Full lips, curves, and that classic Romantic allure and softness." },
      { name: "Drew Barrymore", description: "Soft features, warm presence, and romantic feminine energy." },
      { name: "Christina Hendricks", description: "Lush curves and soft features—Romantic essence with dramatic height." },
      { name: "Dolly Parton", description: "Unapologetically feminine, soft, and romantically styled always." },
    ],
    outfitIdeas: [
      {
        name: "Soft Power",
        occasion: "Work/Professional",
        description: "Feminine authority that commands respect",
        pieces: ["Wrap blouse in soft pink", "Fitted pencil skirt", "Kitten heels", "Pearl earrings", "Soft structured bag"],
      },
      {
        name: "Weekend Romance",
        occasion: "Casual/Brunch",
        description: "Relaxed but still beautifully feminine",
        pieces: ["Soft floral dress", "Cardigan", "Ballet flats", "Delicate necklace", "Soft crossbody bag"],
      },
      {
        name: "Evening Glamour",
        occasion: "Date Night/Events",
        description: "Full Romantic sensuality",
        pieces: ["Draped velvet dress", "Statement earrings", "Strappy heels", "Soft waves hairstyle", "Rose lip color"],
      },
    ],
    makeupStyle:
      "Soft, feminine, and sensual. Glowing skin, softly defined eyes, full lashes, and luscious lips (rose, pink, berry tones). Avoid harsh lines or overly matte finishes—everything should look soft, touchable, and romantic.",
    hairstyles: [
      "Soft, romantic waves",
      "Loose curls",
      "Glamorous Hollywood waves",
      "Soft updos",
      "Feminine, flowing styles",
      "Face-framing layers",
      "Romantic side-swept looks",
    ],
    commonBlends: [
      { essence: "Classic", description: "Soft Classic—romantic femininity with refined elegance." },
      { essence: "Ingenue", description: "Romantic Ingenue—soft, dreamy, and sweetly feminine." },
      { essence: "Dramatic", description: "Soft Dramatic—sensual curves with bold, striking presence." },
    ],
    vsOtherEssences: [
      { essence: "Dramatic", difference: "Romantic is soft and curved; Dramatic is sharp and angular. Romantic flows; Dramatic structures. Complete opposites." },
      { essence: "Natural", difference: "Romantic is sensual and ornate; Natural is casual and relaxed. Romantic adorns; Natural simplifies." },
      { essence: "Ingenue", difference: "Romantic is sensual and womanly; Ingenue is sweet and girlish. Romantic is alluring; Ingenue is innocent." },
    ],
    faqs: [
      {
        question: "What characterizes Romantic essence?",
        answer:
          "Romantic is deep yin—soft, curved, and sensually feminine. People with this essence have rounded features, full lips, large eyes, and lush curves. They look best in draped, curve-honoring clothing with soft, luxurious fabrics like silk, velvet, and lace.",
      },
      {
        question: "What facial features do Romantics have?",
        answer:
          "Look for rounded features (full lips, soft cheeks, large eyes), a curved body with soft flesh, and a naturally feminine, sensual presence. Angular, stiff clothing makes Romantics look harsh, while soft, draped pieces make them glow.",
      },
      {
        question: "Can you be Romantic essence and wear pants?",
        answer:
          "Absolutely! The key is choosing pants that honor your curves—soft, fitted fabrics, rounded details, and styles that flow rather than restrict. Wide-leg pants in soft fabrics, fitted trousers with gentle draping, or soft joggers can all work beautifully.",
      },
      {
        question: "Romantic vs Soft Gamine: what's the distinction?",
        answer:
          "Romantic is pure yin softness—flowing, sensual, and womanly. Soft Gamine has yin softness but also yang contrast—youthful, playful, with sharp elements mixed in. Romantic wears flowing maxi dresses; Soft Gamine wears cropped, contrasting pieces with soft touches.",
      },
    ],
    seasonConnection:
      "Romantic essence harmonizes with soft, warm color seasons like Soft Autumn and True Autumn, as well as rich seasons like Deep Autumn. These palettes have the warmth and richness that match Romantic's lush energy.",
    kibbeConnection:
      "Aligns with Kibbe Romantic, Soft Dramatic, and Theatrical Romantic body types. The soft curves and yin dominance of Romantic essence often mirror these body types.",
  },

  ingenue: {
    id: "ingenue",
    name: "Ingenue",
    yinYang: "yin",
    yinYangLevel: "Delicate Yin",
    tagline: "Sweet, youthful, and delicately charming.",
    description:
      "The Ingenue essence is delicate yin—sweet, youthful, and innocently charming. People with Ingenue essence have small, delicate features, large eyes, and an eternally youthful appearance. They look best in feminine, detailed clothing with small-scale elements and soft, sweet styling.",
    extendedDescription: `Ingenue essence is the essence of sweetness and youthful charm. If you have this essence, you probably look younger than your age and have an innocent, approachable quality that puts people at ease. Your features are delicate and doll-like—large eyes, small nose, soft rounded cheeks.

The key to Ingenue style is delicate femininity with small-scale details. Overwhelming, bold pieces swallow you up. You shine in sweet details—ribbons, bows, lace, soft ruffles—that might look too cute on others but look perfectly right on you.

Ingenue essence people should embrace their youthful quality rather than fighting it. Yes, you can be taken seriously while wearing a bow. Yes, you can be powerful in soft pink. Your charm is your superpower—own it.`,
    facialFeatures: [
      "Large, wide-set eyes",
      "Small, delicate nose",
      "Soft, rounded cheeks",
      "Small, rounded lips",
      "Short or rounded face shape",
      "Youthful appearance",
      "Doll-like features",
      "Innocent, sweet expression",
    ],
    bodyCharacteristics: [
      "Petite, delicate frame",
      "Small bone structure",
      "Soft, rounded curves",
      "Small hands and feet",
      "Short to moderate height",
      "Youthful proportions",
      "Delicate, dainty appearance",
    ],
    energyWords: [
      "Sweet",
      "Youthful",
      "Innocent",
      "Charming",
      "Delicate",
      "Gentle",
      "Fresh",
      "Adorable",
      "Angelic",
      "Approachable",
    ],
    styleKeywords: [
      "Delicate",
      "Small-scale",
      "Feminine",
      "Sweet",
      "Detailed",
      "Pretty",
      "Soft",
      "Youthful",
      "Dainty",
      "Romantic",
    ],
    colorPalette: [
      { name: "Soft Pink", hex: "#FFB6C1", description: "Your signature color—sweet, feminine, and youthful" },
      { name: "Cream", hex: "#FFFDD0", description: "Soft and innocent, perfect for Ingenue delicacy" },
      { name: "Baby Blue", hex: "#89CFF0", description: "Gentle and fresh, ideal for your sweet energy" },
      { name: "Soft Lavender", hex: "#E6E6FA", description: "Dreamy and feminine, flatters your coloring" },
      { name: "Peach", hex: "#FFCBA4", description: "Warm and youthful, brings out your glow" },
      { name: "Mint Green", hex: "#98FF98", description: "Fresh and sweet, perfect for spring/summer" },
    ],
    keyPieces: [
      "Bow-detailed blouse",
      "A-line skirt",
      "Peter Pan collar dress",
      "Lace-trimmed camisole",
      "Cardigan with sweet details",
      "Mary Jane shoes",
      "Fit-and-flare dress",
      "Delicate jewelry",
      "Small structured bag",
      "Ribbon accessories",
    ],
    fabricsAndTextures: [
      "Soft cotton",
      "Delicate lace",
      "Fine knits",
      "Silk chiffon",
      "Eyelet",
      "Soft tulle",
      "Light wool",
      "Gentle textures",
      "Smooth, soft fabrics",
    ],
    patternsAndPrints: [
      "Small florals",
      "Delicate dots",
      "Tiny prints",
      "Soft plaids",
      "Sweet gingham",
      "Small-scale patterns",
      "Toile",
      "Liberty prints",
    ],
    accessories: [
      "Dainty jewelry",
      "Ribbon hair accessories",
      "Small, structured bags",
      "Mary Janes",
      "Delicate watches",
      "Pearl jewelry",
      "Bow details",
      "Hair clips and headbands",
    ],
    avoidStyles: [
      "Large, overwhelming pieces",
      "Bold, aggressive styling",
      "Harsh, dark colors",
      "Oversized, shapeless clothes",
      "Severe, minimal looks",
      "Heavy, chunky accessories",
      "Mature, sophisticated styling",
      "Anything that overwhelms your delicacy",
    ],
    celebrities: [
      { name: "Amanda Seyfried", description: "Large, doe eyes and delicate features—quintessential Ingenue sweetness." },
      { name: "Elle Fanning", description: "Ethereal, youthful beauty with that signature Ingenue innocence." },
      { name: "Dakota Fanning", description: "Delicate features and sweet presence that define Ingenue essence." },
      { name: "Carey Mulligan", description: "Pixie-like delicacy and youthful charm—modern Ingenue icon." },
      { name: "Emmy Rossum", description: "Large eyes, delicate features, and sweet, romantic femininity." },
    ],
    outfitIdeas: [
      {
        name: "Sweet Professional",
        occasion: "Work/Office",
        description: "Taken seriously while staying true to your essence",
        pieces: ["Bow-neck blouse in soft pink", "A-line midi skirt", "Kitten heel pumps", "Delicate pearl studs", "Structured small bag"],
      },
      {
        name: "Weekend Sweetness",
        occasion: "Casual/Brunch",
        description: "Effortlessly charming for everyday",
        pieces: ["Floral sundress", "Light cardigan", "Mary Jane flats", "Ribbon hair accessory", "Small crossbody bag"],
      },
      {
        name: "Evening Charm",
        occasion: "Date/Special Occasion",
        description: "Romantic and dreamy for special moments",
        pieces: ["Soft chiffon dress", "Delicate drop earrings", "Strappy heels", "Pearl bracelet", "Soft waves hairstyle"],
      },
    ],
    makeupStyle:
      "Soft, sweet, and youthful. Dewy skin, soft pink or peach blush, gentle eye definition, and natural-looking lips (pink, peach, rose). Avoid heavy, dramatic makeup—keep it fresh and innocent-looking.",
    hairstyles: [
      "Soft waves",
      "Loose curls",
      "Half-up styles with ribbons",
      "Soft bangs",
      "Ponytails with bows",
      "Braids",
      "Romantic updos",
      "Natural, bouncy styles",
    ],
    commonBlends: [
      { essence: "Romantic", description: "Romantic Ingenue—sweet and sensual, innocent with depth." },
      { essence: "Classic", description: "Classic Ingenue—refined sweetness, Grace Kelly youthfulness." },
      { essence: "Gamine", description: "Soft Gamine—sweet with playful edge, youthful with spirit." },
    ],
    vsOtherEssences: [
      { essence: "Romantic", difference: "Ingenue is sweet and innocent; Romantic is sensual and womanly. Ingenue is girlish; Romantic is alluring." },
      { essence: "Gamine", difference: "Ingenue is soft and delicate; Gamine is sharp and playful. Ingenue is sweet; Gamine is spirited." },
      { essence: "Ethereal", difference: "Ingenue is youthful and sweet; Ethereal is otherworldly and mystical. Ingenue is innocent; Ethereal is transcendent." },
    ],
    faqs: [
      {
        question: "What makes Ingenue essence unique?",
        answer:
          "Ingenue is delicate yin—sweet, youthful, and innocently charming. People with this essence have large eyes, small delicate features, and a naturally youthful appearance. They look best in feminine, detailed clothing with small-scale elements like bows, lace, and soft florals.",
      },
      {
        question: "What features indicate Ingenue essence?",
        answer:
          "Look for large, wide-set eyes, small delicate features, a youthful appearance (people think you're younger), and feeling most yourself in sweet, feminine details. Bold, severe clothing overwhelms Ingenues and makes them look like they're playing dress-up.",
      },
      {
        question: "Is Ingenue essence taken seriously professionally?",
        answer:
          "Absolutely! Your sweet appearance is not a weakness—it's a superpower. You can be powerful, authoritative, and successful while honoring your essence. The key is dressing authentically rather than trying to look 'tougher' than you are.",
      },
      {
        question: "Can older women have Ingenue essence?",
        answer:
          "Essence doesn't expire! If you have Ingenue features, you have them at any age. Many women with Ingenue essence maintain a youthful quality throughout their lives. The key is adapting the styling (perhaps fewer bows, more refined details) while keeping the delicate, feminine core.",
      },
    ],
    seasonConnection:
      "Ingenue essence harmonizes with light, soft color seasons like Light Spring, Light Summer, and Soft Summer. These gentle, fresh palettes match Ingenue's delicate, youthful energy.",
    kibbeConnection:
      "Can appear in any Kibbe type but often aligns with Romantic, Soft Gamine, and Soft Classic. Ingenue is about facial features and energy, which may or may not match body type.",
  },

  ethereal: {
    id: "ethereal",
    name: "Ethereal",
    yinYang: "yin",
    yinYangLevel: "Transcendent Yin",
    tagline: "Otherworldly, mystical, and hauntingly beautiful.",
    description:
      "The Ethereal essence is the most yin of all essences—otherworldly, mystical, and seemingly from another time or realm. People with Ethereal essence have elongated, delicate features and an otherworldly quality that's hard to define but impossible to ignore. They look best in flowing, draped, almost mythical clothing.",
    extendedDescription: `Ethereal essence is the rarest and most mystical of the essences. If you have this essence, you probably get told you look like you're 'not of this world' or 'from another era.' Your beauty is haunting, memorable, and almost supernatural in quality.

The key to Ethereal style is flowing, draped, mystical pieces that enhance your otherworldly quality. You're not meant for harsh, structured, or overly practical clothing. You need fabrics that float and flow, colors that seem to shimmer, and silhouettes that belong in a fairy tale or Pre-Raphaelite painting.

Ethereal essence people often feel like they never quite fit into ordinary fashion. That's because ordinary fashion isn't meant for you—you need extraordinary pieces that match your extraordinary presence.`,
    facialFeatures: [
      "Elongated, delicate features",
      "Large, luminous eyes",
      "Long, straight nose",
      "High, delicate cheekbones",
      "Thin, ethereal lips",
      "Otherworldly expression",
      "Waif-like quality",
      "Seemingly ageless",
    ],
    bodyCharacteristics: [
      "Long, narrow frame",
      "Delicate bone structure",
      "Elongated limbs",
      "Tall or appears tall",
      "Minimal curves",
      "Waif-like silhouette",
      "Graceful, floating movement",
    ],
    energyWords: [
      "Otherworldly",
      "Mystical",
      "Ethereal",
      "Haunting",
      "Dreamlike",
      "Transcendent",
      "Angelic",
      "Mysterious",
      "Timeless",
      "Enchanting",
    ],
    styleKeywords: [
      "Flowing",
      "Draped",
      "Mystical",
      "Ethereal",
      "Shimmery",
      "Layered",
      "Soft",
      "Romantic",
      "Otherworldly",
      "Enchanting",
    ],
    colorPalette: [
      { name: "Silver", hex: "#C0C0C0", description: "Luminous and otherworldly, your signature metallic" },
      { name: "Soft Lilac", hex: "#C8A2C8", description: "Mystical and dreamy, perfect for your essence" },
      { name: "Seafoam", hex: "#93E9BE", description: "Ethereal and watery, mermaid-like quality" },
      { name: "Soft Gold", hex: "#D4AF37", description: "Warm luminosity that enhances your glow" },
      { name: "Icy Blue", hex: "#A5F2F3", description: "Cool and otherworldly, fairy-tale perfect" },
      { name: "Soft White", hex: "#FAF9F6", description: "Pure and angelic, your most essential neutral" },
    ],
    keyPieces: [
      "Flowing maxi dresses",
      "Draped gowns",
      "Sheer layered pieces",
      "Soft, flowing blouses",
      "Long, ethereal skirts",
      "Wing-like sleeves",
      "Delicate jewelry",
      "Soft, minimal heels",
      "Flowing capes or kimonos",
      "Romantic outerwear",
    ],
    fabricsAndTextures: [
      "Chiffon",
      "Organza",
      "Sheer fabrics",
      "Silk",
      "Fine lace",
      "Tulle",
      "Soft metallics",
      "Gossamer textures",
      "Floating fabrics",
    ],
    patternsAndPrints: [
      "Subtle shimmer",
      "Watercolor prints",
      "Celestial patterns",
      "Soft, muted florals",
      "Abstract flowing prints",
      "Iridescent finishes",
      "Nature-inspired ethereal prints",
    ],
    accessories: [
      "Delicate, symbolic jewelry",
      "Crystal or moonstone pieces",
      "Flowing scarves",
      "Ethereal headpieces",
      "Soft, minimal shoes",
      "Transparent or iridescent bags",
      "Nature-inspired pieces",
      "Mystical symbols",
    ],
    avoidStyles: [
      "Heavy, structured pieces",
      "Harsh, bold patterns",
      "Overly practical clothing",
      "Sharp, angular silhouettes",
      "Dark, heavy colors",
      "Chunky accessories",
      "Sporty or casual wear",
      "Anything that grounds you too much",
    ],
    celebrities: [
      { name: "Cate Blanchett (as Galadriel)", description: "The quintessential Ethereal—otherworldly, luminous, and seemingly immortal." },
      { name: "Tilda Swinton", description: "Otherworldly and androgynous, transcends ordinary beauty standards." },
      { name: "Nicole Kidman", description: "Luminous, porcelain beauty with an ethereal, almost supernatural quality." },
      { name: "Saoirse Ronan", description: "Waif-like, ethereal features and that unmistakable otherworldly presence." },
      { name: "Florence Welch", description: "Mystical, bohemian Ethereal—Pre-Raphaelite muse come to life." },
    ],
    outfitIdeas: [
      {
        name: "Modern Mystical",
        occasion: "Work/Creative Fields",
        description: "Otherworldly elegance for professional settings",
        pieces: ["Flowing silk blouse", "Wide-leg soft trousers", "Delicate silver jewelry", "Soft pointed-toe flats", "Sheer overlay piece"],
      },
      {
        name: "Everyday Enchantment",
        occasion: "Casual/Daily",
        description: "Ethereal ease for everyday moments",
        pieces: ["Soft, flowing midi dress", "Light, draped cardigan", "Minimal sandals", "Crystal pendant", "Natural, flowing hair"],
      },
      {
        name: "Evening Transcendence",
        occasion: "Formal/Special Events",
        description: "Full ethereal magic for special moments",
        pieces: ["Flowing chiffon gown", "Delicate drop earrings", "Barely-there heels", "Soft, glowing makeup", "Loose, romantic updo"],
      },
    ],
    makeupStyle:
      "Luminous, glowing, and otherworldly. Dewy, lit-from-within skin, soft shimmer on eyes, and natural-looking lips with subtle luminosity. Think fairy or angel—nothing heavy, everything glowing.",
    hairstyles: [
      "Long, flowing waves",
      "Loose, romantic styles",
      "Soft, face-framing layers",
      "Ethereal updos",
      "Natural, undone looks",
      "Braids with soft pieces loose",
      "Middle parts",
    ],
    commonBlends: [
      { essence: "Dramatic", description: "Dramatic Ethereal—otherworldly with striking presence, high fashion editorial energy." },
      { essence: "Romantic", description: "Romantic Ethereal—soft, dreamy, Pre-Raphaelite beauty." },
      { essence: "Ingenue", description: "Ingenue Ethereal—delicate, angelic, fairy-tale sweetness." },
    ],
    vsOtherEssences: [
      { essence: "Dramatic", difference: "Ethereal is soft and mystical; Dramatic is sharp and powerful. Ethereal floats; Dramatic commands." },
      { essence: "Romantic", difference: "Ethereal is otherworldly and transcendent; Romantic is sensual and earthy. Ethereal is angelic; Romantic is goddess-like." },
      { essence: "Ingenue", difference: "Ethereal is mystical and timeless; Ingenue is sweet and youthful. Ethereal seems from another world; Ingenue seems forever young." },
    ],
    faqs: [
      {
        question: "What is Ethereal essence in style analysis?",
        answer:
          "Ethereal is the most yin essence—otherworldly, mystical, and hauntingly beautiful. People with Ethereal essence have elongated, delicate features and an otherworldly quality. They look best in flowing, draped, mystical clothing with luminous, shimmery elements.",
      },
      {
        question: "How can you tell if someone is Ethereal?",
        answer:
          "Look for elongated, delicate features, luminous quality to the appearance, an otherworldly or 'out of time' look, and feeling most authentic in flowing, mystical clothing. Practical, structured clothing makes Ethereals feel ordinary and not themselves.",
      },
      {
        question: "Why is Ethereal considered the rarest essence?",
        answer:
          "Ethereal is considered the rarest of the seven essences. Most people have it as a secondary essence blended with another (like Dramatic Ethereal or Romantic Ethereal) rather than as their primary essence. Pure Ethereal essence is quite uncommon.",
      },
      {
        question: "Do Ethereals have to be tall?",
        answer:
          "No! While many Ethereals have elongated features that create an impression of height, essence is about your overall quality, not actual height. A petite person with otherworldly, luminous features and that mystical quality can absolutely have Ethereal essence.",
      },
    ],
    seasonConnection:
      "Ethereal essence harmonizes with soft, luminous color seasons like Light Summer, Soft Summer, and Light Spring. Cool, soft seasons with shimmer potential match Ethereal's mystical energy.",
    kibbeConnection:
      "Can appear in any Kibbe type but often aligns with Dramatic, Soft Dramatic, or Romantic body types. Ethereal is primarily about facial features and energy rather than body line.",
  },
};

// Export essence IDs for generateStaticParams
export const essenceIds = Object.keys(styleEssences);

// Get essence by ID
export function getStyleEssence(id: string): StyleEssence | undefined {
  return styleEssences[id];
}

// Get all essences as array
export function getAllEssences(): StyleEssence[] {
  return Object.values(styleEssences);
}
